import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { TiMetricsActionEventDetail, TiMetricsTrackable, TiMetricsApplication, TiMetricsDataStatus, TiMetricsTracker, TiResponsive, TiResponsiveViewport, TiResponsiveHandler } from '@ticom/web-component-utils';
import { HTMLTiInputElement, HTMLTiSelectElement } from '@ticom/ui-components/dist';
import { CurrencyPricing, TieredPricing, AddToCartChangeEventDetail, AddToCartResponseErrorType, AddToCartResponse} from './add-to-cart.interface';
import { loadGlobalStyle } from '@ticom/web-component-utils/dist';
import DAMImageUtil from '../../global/imagemetadataapi/dam-image-util';
import { default as languageStrings } from '../../global/l10n/AddToCart.json';

/**
 * @obsolete Property {apiUrl} was deprecated before seamless launch and was replaced by property {addToCartUrl} which is now @obsolete , so {apiUrl} is also obsoleted
 * @obsolete Property {addToCartUrl} is now no more used and it is replaced by private variable _addToCartUrl which uses the new cartmodel addtocart endpoint
 * @obsolete Property {checkoutUrl} is now no more used and is now formed internally by this ATC comp and for this completeCheckoutUrl varible is used
 */
@Component({
    tag: 'ti-add-to-cart',
    styleUrl: 'add-to-cart.scss',
    shadow: true
})
export class AddToCart implements TiMetricsTrackable, TiResponsive {

    private static readonly _BUTTON_STATUS_TYPE = {
        ERROR: 'ERROR',
        OK: 'OK'
    }

    /**
     * Api URL - where we send the add to cart information to store ordering engine.
     *
     * For local testing replace the value of this private variable by given as following from '/cartmodel/cart/add-to-cart':
     * '/data/ti-add-to-cart/success.json'              :  for success response
     * '/data/ti-add-to-cart/error.json'                :  for generic error response
     * '/data/ti-add-to-cart/error-full-cart.json'      :  for response when cart has exceed its limit
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _addToCartUrl: string = '/cartmodel/cart/add-to-cart';

    private _atcApiRespErrorType: AddToCartResponseErrorType = AddToCartResponseErrorType.GENERIC;

    /**
     * batch code for HiRel
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _batchCode: string;

    private _currencyExchangeRates: { [currency: string]: number };

    // global utility function to format price
    private _currencyFormat: any = { convertAndFormat: price => price, format: price => price };

    // locale for currency formatting
    private _currencyLocale: string = 'en-US';

    /**
     * custom_reel value from custom-reel API response.
     * Used to determine whether to show custom-reel or bypass to add-to-cart
     *
     * @type {boolean}
     */
    private _customReel: boolean;

    /**
     * Custom reel fee div element - used to set custom reel fee message.
     *
     * @private
     * @type {HTMLDivElement}
     * @memberof AddToCart
     */
    private _customReelFeeElement: HTMLDivElement;

    /**
     * The date code label that the user has selected
     *
     * @type {string}
     * @memberof AddToCart
     */
    private _dateCodeLabel: string;

    /**
     * The fee associated to the date code that the user has selected
     *
     * @type {string}
     * @memberof AddToCart
     */
    private _dateCodeFee: string;

    /**
     * The possible date codes available for the part
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _dateCodes = {};

    /**
     * dialog launcher service
     */
    private _dialogService;

    /**
     * The option values available for lot date codes
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _dienOptions: string;

    /**
     * The failMessage is private because we don't want any risks of someone
     * injecting HTML into our components.
     *
     * @type {string}
     */
    private _failMessage: string;

    /**
     *This fail message variable is used store the error message recieved from
     * add to cart API response when cart has crossed max number of line items.
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _failMessageOnCartFull: string;

    /**
     *This fail message is displayed to the user in the error dialog when we get
     * on recieving a 401 un-authorized response from add to cart api
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _failMessageOnUnauthorized: string;

    /**
     * The unit price of a OPN
     * We will get this from the service and populate the dialog with it.
     * @type {string}
     */
    private _formattedUnitPrice: string;

    /**
     * Error message content indicating cart already being full
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _fullCartErrorDialogContent: string;

    /**
     *Title of the Error Dialog when error message related to cart already being full is displayed
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    private _fullCartErrorDialogTitle: string;

    /**
     *  full_reel value from custom-reel API response.
     * # of full_reel available for given opn
     *
     * @type {number}
     */
    private _fullReel: number;

    /**
     * The description of the GPN.
     * We will get this from the service and populate the dialog with it.
     *
     * @type {string}
     */
    private _gpnDescription: string;

    /**
     * The image URL of the GPN
     * We will get this from the service and populate the dialog with it.
     *
     * @type {string}
     */
    private _gpnImageUrl: string;

    /**
     * For inStickyColumn, the initial height that this will be restored to
     * @type {number}
     */
    private _initialHeight: number;

    /**
     * The quantity field input element - for resetting on changes.
     *
     * @private
     * @type {HTMLTiInputElement}
     * @memberof AddToCart
     */
    private _inputElement: HTMLTiInputElement;

    /**
     * flag to check if add-to-cart dialog is open
     * if the dialog is already open then update the config, else open a new dialog
     */
    private _isDialogOpen: boolean = false;

    /**
     * lowercase locale code taken from locale
     * ex. ja-JP = ja-jp, special case en-US = ''
     */
    private _languageCode: string;

    /* Locale-specific set of language strings */
    private _localeStrings: Record<string, string>;

    /* Login button label */
    private _loginLabel: string;

    /**
     * Url for fallback image when product image url is not available or image fails to load in ti-image.
     */
    private _noImagePlaceholder = '/content/dam/ticom/images/icons/illustrative-icons/miscellaneous/no-image-available-icon.png';

    /**
     * OPN URL for cases where the add-to-cart is not located in PF, it should provide a link to the TI store page
     *
     * @type {string}
     */
    private _opnUrl: string;

    /**
     * The PCR codes that are valid
     *
     * @private
     * @type {[string]}
     * @memberof AddToCart
     */
    private _pcrCodes: [string];

    /**
     * package option selected by user for custom reels
     *
     * @type {number}
     */
    private _reelCustom: number = 0;

    /**
     * package option selected by user for custom reels
     *
     * @type {number}
     */
    private _reelCutTape: number = 0;

    /**
     * package option selected by user for custom reels
     *
     * @type {string}
     */
    private _reelPkgOption: string = null;

    /**
     * The week field select element in Hi-Rel dialog - for resetting it on year chage.
     *
     * @private
     * @type {HTMLTiSelectElement}
     * @memberof AddToCart
     */
    private _selectElement: HTMLTiSelectElement;

    private _viewCartButtonLabel: string ;

    @TiMetricsTracker()
    @Element() hostElement: HTMLTiAddToCartElement;

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @type {string}
     * @default this.buttonStatusType.OK
     */
    @State() buttonStatus: string = AddToCart._BUTTON_STATUS_TYPE.OK;

    /**
     * The final checkout URL to redirect to the store cart
     *
     * @type {string}
     */
    @State() completeCheckoutUrl: string;

    /**
     * Keep track of the custom reel input status
     *
     * @type {boolean}
     */
    @State() completedCustomReel: boolean = false;

    /**
     * Keep track of the hi rel input status
     *
     * @type {boolean}
     * @memberof AddToCart
     */
    @State() completedHiRel: boolean = false;

    /**
     * Keep track of the status of the dialog data so that we can render
     * the dialog accordingly.
     *
     * @type {TiMetricsDataStatus}
     * @default TiMetricsDataStatus.NONE
     */
    @State() dataStatus: TiMetricsDataStatus = TiMetricsDataStatus.NONE;

    /**
     * The date code that the user has selected
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() dateCode: string;

    /**
     * The Lot Code week selected by user
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() dateCodeWeek: string;

    /**
     * The option labels available for date code weeks
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() dateCodeWeekOptions: string;

    /**
     * The Lot Code year selected by user
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() dateCodeYear: string;

    /**
     * The option labels available for date code years
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() dateCodeYearOptions: string;

    /**
     * The option labels available for lot date codes
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() dienLabels: string;

    /**
     * Keeps track of hi-rel selection completion based on user selection
     *
     * @type {boolean}
     * @memberof AddToCart
     */
    @State() hiRelIncomplete: boolean = true;

    /**
     * flag to check if the user has selected date code
     * if false, then the user has selected PCR code
     *
     * @private
     * @type {boolean}
     * @memberof AddToCart
     */
    @State() isDateCode: boolean = true;

    /**
     * The PCR code the user entered
     *
     * @private
     * @type {string}
     * @memberof AddToCart
     */
    @State() pcrCode: string;

    /**
     * Flag for whether the PCR code entered is invalid
     *
     * @private
     * @type {boolean}
     * @memberof AddToCart
     */
    @State() pcrCodeError: boolean;

    /**
     * comma separated quantity display for successful add-to-cart action
     *
     * @type {string}
     */
    @State() quantityDisplay: string;

    /**
     * Keep track of the quantity input status
     *
     * @type {boolean}
     */
    @State() quantityError: boolean;

    /**
     * quantity error text when allowing user to enter quantity input
     *
     * @type {string}
     */
    @State() quantityErrorText: string;

    /**
     * viewport size state required for TiResponsive
     */
    @TiResponsiveHandler()
    @State() viewportSize: TiResponsiveViewport;

    /**
     * Sets the appearance for add-to-cart's ti-button
     * @type {string}
     * @default "primary"
     */
    @Prop() appearance: string = 'primary';

    /**
     * Application type tells the checkout modal window whether the part is a
     * product or a tool in order to properly fetch the description of the product.
     * It also sets the default quantity for product or tool.
     * "PF" is for product and "TF" is for tool
     *
     * @type {string} "PF" or "TF"
     */
    @Prop() applicationType: string = 'PF';
    @Watch('applicationType')
    handleApplicationTypeChange() {
        // update anything set during load that is based on applicationType
        this.handleShowQuantityInputChange(this.showQuantityInput);
    }

    /**
     * Label for button.
     *
     * @type {string}
     * @default "Add to cart"
     */
    @Prop({ mutable: true }) callToActionLabel: string;

    /**
     * Label for "added to cart" dialog "Checkout" button.
     *
     * @type {string}
     * @default "Checkout"
     */
    @Prop({ mutable: true }) checkoutLabel: string;

    /**
     * If the button for add-to-cart should be a compact button variant.
     *
     * @type {boolean}
     * @default false
     */
    @Prop() compactButton: boolean = false;

    /**
     * Label for "carrier options" dialog "Continue" button.
     *
     * @type {string}
     * @default "Continue"
     */
    @Prop({ mutable: true }) continueLabel: string;

    /**
     * Label for "added to cart" dialog "Continue shopping" button.
     *
     * @type {string}
     * @default "Continue shopping"
     */
    @Prop({ mutable: true }) continueShoppingLabel: string;

    /**
     * Currency to be displayed
     *
     * @type {Currency}
     * @default 'USD'
     */
    @Prop({ mutable: true }) currency: string = 'USD';

    /**
     * Currency Exchange Rate URL
     * Don't set this if you don't have to.
     *
     * @type {string}
     * @memberof AddToCart
     * @default '/productmodel/currencyExchangeRates'
     */
    @Prop() currencyExchangeRateUrl: string = '/productmodel/currencyExchangeRates';

    /**
     * Exchange Rate for currency
     *
     * @types {string}
     */
    @Prop() currencyExchangeRateJson: string;

    /**
     * Custom Reels Service URL
     * Don't set this if you don't have to.
     *
     * @type {string}
     * @default '/productmodel/customreel/inventoryforecast'
     */
    @Prop() customReelsApiUrl: string = '/productmodel/customreel/inventoryforecast';

    /**
     * Custom Reels Fee URL
     * Don't set this if you don't have to.
     *
     * @type {string}
     * @memberof AddToCart
     * @default '/productmodel/customReelFees'
     */
    @Prop() customReelFeeUrl: string = '/productmodel/customReelFees';

    /**
     * message for custom reels dialog.
     *
     * @type {string}
     * @default ""
     */
    @Prop({ mutable: true }) customReelMessage: string = '';

    /**
     * JSON object with custom reels fees defined by currencies
     *
     * @types {string}
     */
    @Prop() customReelFee: string;

    /**
     * message for custom reel fee when custom reels option is selected in dialog.
     * @type {string}
     * @default ""
     */
    @Prop({ mutable: true }) customReelFeeMessage: string = '';

    /**
     * Data Lid for metrics tracking.
     * Please validate with MGB (metrics governing board) to ensure this has the
     * right value for your intended usage.
     *
     * @type {string}
     */
    @Prop() dataLid: string;

    /**
     * the API url to retrieve the description, unit price, and image url
     *
     * @type {string}
     * @default "/popularorderables/addtocart/"
     */
    @Prop() descriptionApiUrl: string = '/popularorderables/addtocart/';

    /**
     * Header for dialog when add to cart fails.
     *
     * @type {string}
     * @default "Something went wrong"
     */
    @Prop({ mutable: true }) failHeader: string;

    /**
     * Hide carrier (Custom reels) and sku (lot/date) messages
     *
     * @type {boolean}
     * @default false
     */
    @Prop() hideCarrierSkuMessage: boolean = false;

    /**
     * Hide quantity hint text line
     *
     * @type {boolean}
     * @default false
     */
    @Prop() hideQuantityHintText: boolean = false;

    /**
     * GPN is required for metrics tracking purposes.
     * Until the "add to cart" web service is ready and providing
     * the GPN to us, we will have to manually provide this so
     * that metrics can be attributed appropriately.
     *
     * @type {string}
     */
    @Prop() gpn: string;

    /**
     * the API url to retrieve the hi-rel and custom-reel information
     *
     * @type {string}
     * @memberof AddToCart
     * @default '/occservices/v2/ti/prostInventory'
     */
    @Prop() prostInventoryUrl: string = '/occservices/v2/ti/prostInventory';

    /**
     * the API url to retrieve imagemetadata for parts
     *
     * @type {string}
     * @memberof AddToCart
     * @default '/imagemetadata/api'
     */
    @Prop() imagemetadataApiUrl: string = '/imagemetadata/api';

    /**
     * To set button and inputfield horizontally inline
     *
     * @type {boolean}
     * @default false
     */
    @Prop() inline: boolean = false;

    /**
     * inStickycolumn so that when an error message shows/hide the parent
     * TR will update its height accordingly
     *
     * @type {boolean}
     * @default false
     */
    @Prop() inStickycolumn: boolean = false;

    /**
     * Locale.
     *
     * @type {string} Possible values include 'en-US','zh-CN','ja-JP'.
     * @default 'en-US'
     */
    @Prop({ mutable: true }) locale: string = 'en-US';

    /**
     * Orderable part number.
     *
     * @type {string}
     */
    @Prop() opn: string;
    @Watch('opn')
    handleOpnChange(newValue: string, oldValue: string) {
        // clear the quantity field
        this._inputElement.value = '';
        // update anything set during load that is based on OPN
        if (this._failMessage) {
            this._failMessage = this._failMessage.replace(oldValue, newValue);
        }
    }

    /**
     * Limit for the quantity that can be entered per order.
     *
     * @type {string}
     */
    @Prop() orderLimit: number;
    @Watch('orderLimit')
    handleOrderLimitChange(newValue: number) {
        // update anything set during load that is based on orderLimit
        if (newValue && newValue < this.quantityLimit) {
            const text = this._localeStrings['quantityHint'];
            this.quantityHintText = text.replace('{}', newValue.toLocaleString());
        } else {
            this.quantityHintText = undefined;
        }

        //Revalidate quantity on order-limit or quantity-limit change
        this._verifyQuantity(this.quantity);
    }

    /**
     * Standard 'packageQuantity' value from ProductModel
     *
     * @type {number}
     */
    @Prop() packageQuantity: number;

    /**
     * Part type for Metrics tracking
     *
     * @type {string}
     * @default "ic"
     */
    @Prop() partType: string = 'ic';

    /**
     * Override price display. Will be shown instead of price
     * Used primarily to show "Free sample" for PDP samples pages rather than the unit price which is not applicable
     *
     * @type {string}
     */
    @Prop() priceOverrideText: string;

    /**
     * Status of the OPN (for metrics purposes)
     *
     * @type {string}
     */
    @Prop() productStatus: string;

    /**
     * quantity to be added to the cart
     */
    @Prop({ mutable: true, reflect: true }) quantity: number;

    /**
     * quantity hint text when allowing user to enter quantity input
     *
     * @type {string}
     */
    @Prop({ mutable: true }) quantityHintText: string;

    /**
     * quantity label in the add to cart modal
     *
     * @type {string}
     */
    @Prop({ mutable: true }) quantityLabel: string;

    /**
     * quantity limit for quantity check
     *
     * @type {number}
     */
    @Prop() quantityLimit: number;
    @Watch('quantityLimit')
    handleChange() {
        // update anything set during load that is based on quantityLimit
        this.handleOrderLimitChange(this.orderLimit);
    }

    /**
     * placeholder text in the quantity input
     *
     * @type {string}
     */
    @Prop({ mutable: true }) quantityPlaceholderText: string;

    /**
     * relevantForCustomReel value from productmodel service, if true
     * show "Custom reel may be available" message
     *
     * @type {boolean}
     * @default false
     */
    @Prop() relevantForCustomReel: boolean = false;
    @Watch('relevantForCustomReel')
    handleRelevantForCustomReelChange(newValue: boolean) {
        // update anything set during load that is based on relevantForCustomReel
        this.customReelMessage = newValue ? this._localeStrings['customReelMessage'] : '';
    }

    /**
     * relevantForLotDateCodeFlag value from productmodel service, if true
     * show "Lot and code selection may be available" message
     *
     * @type {boolean}
     * @default false
     */
    @Prop() relevantForLotDateCode: boolean;

    /**
     * flag to show add to list button
     *
     * @type {boolean}
     * @memberof AddToCart
     */
    @Prop() showAddToList: boolean = false;

    /**
     * flag to check whether to show currency code for unit price or not
     * @default false
     */
    @Prop() showCurrencyCode: boolean = false;

    /**
     * flag to check whether or not to use extended pricing precision
     *
     * @default true
     */
    @Prop() showExtendedPrecisionPrice: boolean = true;

    /**
     * A secret key parameter for store, required property for PDP/Store side
     *
     * @type {string}
     * @memberof AddToCart
     */
    @Prop() storeKey: string;

    /**
     * Whether to show quantity input or not
     */
    @Prop() showQuantityInput: boolean = false;
    @Watch('showQuantityInput')
    handleShowQuantityInputChange(newValue: boolean) {
        // update anything set during load that is based on showQuantityInput if quantity has not already been defined
        if (!newValue && this.quantity == null) {
            if (this.applicationType === 'PF') {
                this.quantity = 5;
            } else if (this.applicationType === 'TF') {
                this.quantity = 1;
            }
        }
    }

    /**
     * "Added to cart" header for dialog
     * @type {string}
     * @default "Added to cart"
     */
    @Prop({ mutable: true }) successHeader: string;

    /**
     * Location for add to cart API location tracking for metrics
     * @type {string}
     */
    @Prop() tiMetricsLocation: string;

    /**
     * root URL for ti store for various links in add-to-cart
     * @type {string}
     * @default "https://www.ti.com"
     */
    @Prop() tiStoreUrl: string = 'https://www.ti.com';
    @Watch('tiStoreUrl')
    handleTiStoreUrlChange(newValue: string, oldValue: string) {
        // update anything set during load that is based on tiStoreUrl
        if (this._failMessage) {
            this._failMessage = this._failMessage.replace(oldValue, newValue);
        }
    }

    /**
     * root china URL for ti store for various links in add-to-cart
     * @deprecated no longer required as redirect logic is taken care of on store side based on LLC
     *
     * @type {string}
     * @default "https://www.ti.com.cn"
     */
    @Prop({ mutable: true }) tiStoreChinaUrl: string = 'https://www.ti.com.cn';

    /**
     * unit price label in the add to cart modal
     *
     * @type {string}
     */
    @Prop({ mutable: true }) unitPriceLabel: string;

    /**
     * flag to check whether or not to show the custom reel, if true
     * show "Custom reel with carrier options" modal, else skip to show "Added to cart" modal
     *
     * @type {boolean}
     * @default false
     */
    @Prop() useCustomReelApi: boolean = false;

    /**
     * Event emitted when add to cart makes any changes to the cart.
     *
     * @type {EventEmitter<AddToCartChangeEventDetail>}
     * @memberof AddToCart
     */
    @Event({ bubbles: true, composed: true }) tiAddToCartChange: EventEmitter<AddToCartChangeEventDetail>;

    /**
     * Event for when a metrics actions occurs.
     */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    /**
     * Lifecycle event handler
     */
    async componentWillLoad() {
        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};

        if (typeof this.gpn == 'undefined' || this.gpn === ''
            || typeof this.opn == 'undefined' || this.opn === ''
            || !(this.applicationType in TiMetricsApplication)) {
            this.buttonStatus = AddToCart._BUTTON_STATUS_TYPE.ERROR;
            this.dataStatus = TiMetricsDataStatus.ERROR;
            return;
        }

        // If we are not showing the quantity input,
        // set the appropriate quantity based on product or tool
        this.handleShowQuantityInputChange(this.showQuantityInput);

        // set the language code for the locale - english is a special case
        // all other locales are lower case for language code e.g. 'ja-jp'
        if ('en-US' === this.locale) {
            this._languageCode = '';
        }
        else {
            this._languageCode = this.locale.toLowerCase() + '/';
        }

        if (this.currencyExchangeRateJson) {
            this._currencyExchangeRates = JSON.parse(this.currencyExchangeRateJson);
        } else {
            let currencyExchangeRateResponse;
            try {
                const currencyExchangeRateFetch = await fetch(this.currencyExchangeRateUrl, {
                    credentials: 'include',
                    headers: {
                        'Cache-Control': 'no-store, must-revalidate',
                        'Expires': '0',
                        'Content-Type': 'application/json'
                    },
                    method: 'GET'
                });
                if (!currencyExchangeRateFetch.ok) {
                    throw new Error(`Received ${currencyExchangeRateFetch.status} for addToCart`);
                }
                currencyExchangeRateResponse = await currencyExchangeRateFetch.json();
            } catch (err) {
                // Default to USD if API fails
                this._currencyExchangeRates = { 'USD': 1 };
            }

            if (currencyExchangeRateResponse) {
                this._currencyExchangeRates = currencyExchangeRateResponse;
            }
        }

        // If no custom reel fee is provided hit the custom reel fee URL
        if (this.customReelFee === undefined) {
            let customReelFeeResponse;
            try {
                const customReelFeeFetch = await fetch(this.customReelFeeUrl, {
                    credentials: 'include',
                    headers: {
                        'Cache-Control': 'no-store, must-revalidate',
                        'Expires': '0',
                        'Content-Type': 'application/json'
                    },
                    method: 'GET'
                });
                if (!customReelFeeFetch.ok) {
                    throw new Error(`Received ${customReelFeeFetch.status} for addToCart`);
                }
                customReelFeeResponse = await customReelFeeFetch.text();
            } catch (err) {
                // Failed to get custom reel fee from API
            }

            if (customReelFeeResponse) {
                // Need to replace currencyPrice with currency_price to match feed from TI.com
                this.customReelFee = customReelFeeResponse.replace(/currencyPrice/g, 'currency_price');
            }
        }

        // pre-initialize the currency and locale for currency formatting
        const com = (window as any).com;
        if (com && com.TI && com.TI.UserPreferences) {
            this.currency = await com.TI.UserPreferences.getUserCurrencyPreference() || 'USD';
            this._currencyLocale = await com.TI.UserPreferences.getUserLanguagePreference() || 'en-US';
        }

        // set the default labels if not provided by user
        if (!this.callToActionLabel) this.callToActionLabel = this._localeStrings['callToActionLabel'];
        if (!this.checkoutLabel) this.checkoutLabel = this._localeStrings['checkoutLabel'];
        if (!this._viewCartButtonLabel) this._viewCartButtonLabel = this._localeStrings['viewCartLabel'];
        if (!this.continueLabel) this.continueLabel = this._localeStrings['continueLabel'];
        if (!this.continueShoppingLabel) this.continueShoppingLabel = this._localeStrings['continueShoppingLabel'];
        if (!this.failHeader) this.failHeader = this._localeStrings['failHeader'];
        if (!this._failMessage) this._failMessage = this._localeStrings['failMessage'];
        this._failMessage = this._failMessage.replace('{}', `${this.tiStoreUrl}/product/${this._languageCode}${this.gpn}/part-details/${this.opn}`);
        if (!this._failMessageOnUnauthorized) this._failMessageOnUnauthorized = this._localeStrings['failIdleModeErrorMessage'];
        if (!this._fullCartErrorDialogTitle) this._fullCartErrorDialogTitle = this._localeStrings['fullCartErrorDialogTitle'];
        if (!this._fullCartErrorDialogContent) this._fullCartErrorDialogContent = this._localeStrings['fullCartErrorDialogContent'];
        if (!this._loginLabel) this._loginLabel = this._localeStrings['loginLabel'];
        if (!this.successHeader) this.successHeader = this._localeStrings['successHeader'];
        if (!this.quantityLabel) this.quantityLabel = this._localeStrings['quantity'];
        if (!this.quantityPlaceholderText) this.quantityPlaceholderText = this._localeStrings['quantityPlaceholder'];
        this.handleOrderLimitChange(this.orderLimit);
        if (!this.unitPriceLabel) this.unitPriceLabel = this._localeStrings['unitPrice'];
        this.handleRelevantForCustomReelChange(this.relevantForCustomReel);
        this.buttonStatus = AddToCart._BUTTON_STATUS_TYPE.OK;
        this.dataStatus = TiMetricsDataStatus.NONE;

        this._setCurrencyFormat();

        // check qty if available on load
        if (this.quantity) {
            this._verifyQuantity(this.quantity);
        }
    }

    /*
     * We need this method even if empty as a workaround for a Stencil issue:
     * https://github.com/ionic-team/stencil/issues/1671
     * Without this, our TiMetricsTracker decorator (and metrics tracking) does not work at all.
     * We just need to have at least one implementation of `componentDidLoad` in the bundle.
     */
    componentDidLoad() {
        // load global style for add-to-cart dialog
        loadGlobalStyle('feature-components');
    }

    /**
     * required for TiResponsive
     */
    disconnectedCallback: () => unknown;

    /**
     * Listen to the `currencyChange` event of header-currency-selection
     * @param event
     */
    @Listen('tiCurrencyChange', { target: 'window' })
    async onCurrencyChange(event) {
        this.currency = event.detail;
    }

    /**
     * Listen to the 'tiCurrencyFormatReady' event from CurrencyFormat in order
     * to set the local reference to com.TI.CurrencyFormat.
     */
    @Listen('tiCurrencyFormatReady', { target: 'document' })
    async onCurrencyFormatReady() {
        this._setCurrencyFormat();
    }

    /**
     * Listen to the `currencyReady` event of header-currency-selection
     * @param event
     */
    @Listen('tiCurrencyReady', { target: 'window' })
    async onCurrencyReady(event) {
        this.currency = event.detail;
    }

    /**
     * Fire metrics action on dialog close action
     */
    @Listen('tiDialogClosed', { target: 'window' })
    handleTiDialogClosed() {
        if (this._isDialogOpen) {
            this.completedCustomReel = false;
            this._customReelFeeElement = null;
            this.customReelFeeMessage = '';
            this._reelPkgOption = null;
            this._fullReel = 0;
            this._reelCustom = 0;
            this._reelCutTape = 0;
            this.completedHiRel = false;
            this.dateCode = '';
            this.dateCodeYear = '';
            this.dateCodeWeek = '';
            this._batchCode = '';
            this.pcrCode = '';
            this.isDateCode = true;
            this.dataStatus = TiMetricsDataStatus.NONE;
            this._isDialogOpen = false;
            this.tiMetricsAction.emit({
                elementName: this.hostElement.tagName,
                eventName: 'add to cart modal close'
            });
        }
    }

    /**
     * Listen to languageChange event from ti-header-language-selection
     */
    @Listen('tiLanguageChange', { target: 'window' })
    onTiLanguageChange(event: CustomEvent) {
        this._currencyLocale = event.detail.locale;
    }

    /**
     * Listen to languageReady event from ti-header-language-selection
     */
    @Listen('tiLanguageReady', { target: 'window', capture: true })
    onTiLanguageReady(event: CustomEvent) {
        this._currencyLocale = event.detail.locale;
    }

    /**
     * Handle click event for call to action.
     */
    private async _addToCart() {
        // return if hirel is incomplete when relevant for hirel
        if (this.relevantForLotDateCode && this.hiRelIncomplete) {
            return;
        }

        // return if quantity is incorrect in hirel modal
        if (this.relevantForLotDateCode && isNaN(this.quantity)) {
            this.quantityErrorText = this.quantityErrorText = this._localeStrings['quantityError2'];
            this.quantityError = true;
            // update the hi-rel dialog status
            this._setHiRelCompletion();
            return;
        }

        this._setStatus(TiMetricsDataStatus.LOADING);

        // Default to cut-tape if no carrier option
        if (this._reelPkgOption === null && this.relevantForCustomReel) {
            this._reelPkgOption = 'CTX';
        }
        this.completedCustomReel = true;
        this.completedHiRel = true;

        const cartRequestBody = {
            cartRequestList: [{
                'packageOption': this._reelPkgOption,
                'opnId': this.opn,
                'quantity': this.quantity.toString(),
                'tiAddtoCartSource': this.tiMetricsLocation,
                'dienCode': this.dateCode === 'NONE' ? '' : this.dateCode,
                'year': this.dateCodeYear,
                'week': this.dateCodeWeek,
                'batchCode': this._batchCode,
                'pcrCode': this.pcrCode,
                'sparam': this.storeKey ? this.storeKey : ''
            }],
            'currency': this.currency
        };

        let addToCartResponse: AddToCartResponse;
        try {
            const addToCartFetch = await fetch(this._addToCartUrl, {
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                },
                method: 'POST',
                body: JSON.stringify(cartRequestBody)
            });


            if (addToCartFetch.status === 401) {
                this._atcApiRespErrorType = AddToCartResponseErrorType.UNAUTHORIZED;
                throw new Error(`Received 401 un-authorized for addToCart api. User session invalid.`);
            }
            if (!addToCartFetch.ok) {
                throw new Error(`Received ${addToCartFetch.status} for addToCart`);
            }
            addToCartResponse = await addToCartFetch.json();
            await this._populateCTALinks();

            if (addToCartResponse.statusType === 'ERROR' ) {
                if (addToCartResponse.errorType === AddToCartResponseErrorType.CART_FULL) {
                    this._atcApiRespErrorType = AddToCartResponseErrorType.CART_FULL;
                    this._failMessageOnCartFull = this._fullCartErrorDialogContent;

                    if (addToCartResponse.placeholderMap && addToCartResponse.placeholderMap['limit']) {
                        this._failMessageOnCartFull = this._fullCartErrorDialogContent.replace('{}', addToCartResponse.placeholderMap['limit']);
                    }

                    throw new Error(`Internal error as cart has reached max limit of items`);

                } else {
                    this._atcApiRespErrorType = AddToCartResponseErrorType.GENERIC;
                    this._failMessageOnCartFull = '';

                    throw new Error(`internal error with opn: ${this.opn}`);
                }
            } else if (!addToCartResponse.cartId) {
                throw new Error(`cartId is invalid`);
            }

        } catch (err) {
            this._setStatus(TiMetricsDataStatus.ERROR);
            this.tiMetricsAction.emit({
                elementName: this.hostElement.tagName,
                eventName: 'add to cart',
                status: this.dataStatus,
                error: err.message
            });
            return;
        }

        // emit tiAddToCartChange event (picked up by cart in header and causes count to refresh)
        this.tiAddToCartChange.emit({
            ti_gpn: this.gpn,
            ti_opn: this.opn
        });

        let descResponse;
        try {
            const descFetch = await fetch(`${this.descriptionApiUrl}${this.locale}/${this.applicationType}?opn=${this.opn}`);
            if (!descFetch.ok) {
                throw new Error(`Received ${descFetch.status} for description service`);
            }
            descResponse = await descFetch.json();
            if (!descResponse) {
                throw new Error(`Empty response from for description service`);
            }
        } catch (err) {
            this._setStatus(TiMetricsDataStatus.ERROR);
            this.tiMetricsAction.emit({
                elementName: this.hostElement.tagName,
                eventName: 'add to cart',
                status: this.dataStatus,
                error: err.message
            });
            return;
        }

        this._gpnDescription = descResponse.description;

        if (this.applicationType == 'TF') {
            // get hw image URLs from imagemetadata API
            this._gpnImageUrl = await DAMImageUtil.getInstance(this.imagemetadataApiUrl).getFirstImageUrl(this.opn, this.locale, 'opn', 'TI BOARD PHOTOGRAPHY', 'small');
        }
        else if (this.applicationType == 'PF') {
            // get chipshot image URLs from imagemetadata API
            this._gpnImageUrl = await DAMImageUtil.getInstance(this.imagemetadataApiUrl).getFirstImageUrl(this.opn, this.locale, 'opn', 'IC PACKAGE IMAGE ASSET', 'singlesmall');
        }
        else {
            this._gpnImageUrl = (descResponse.imageurl === 'Y')
                ? `https://www.ti.com/graphics/folders/partimages/${this.gpn}.jpg`
                : descResponse.imageurl;
        }

        const tieredPricing: TieredPricing[] = descResponse.tiered_price_list as TieredPricing[];
        // API will return lowest to highest ranges for tiered pricing
        let tieredPrice: TieredPricing = tieredPricing.find(tieredPrice => tieredPrice.range_to >= this.quantity);

        // If the quantity is greater than the highest tiered price range, default to the highest tier
        if (tieredPrice === undefined) {
            tieredPrice = tieredPricing[tieredPricing.length - 1];
        }

        let rawUnitPrice; // for metrics event
        let metricsCurrency = this.currency; // for metrics event

        // If old description API, we default to USD and read the price the old way
        if (tieredPrice.price !== undefined) {
            rawUnitPrice = Number.parseFloat(tieredPrice.price);
            this._formattedUnitPrice = this._currencyFormat.format(rawUnitPrice, this.showExtendedPrecisionPrice, 'USD', this._currencyLocale);
            metricsCurrency = 'USD';
        } else {
            let currencyPrice = tieredPrice.price_list.find(currencyPrice => currencyPrice.currency === this.currency);
            if (currencyPrice) {
                // we have the price in the right currency - no need to convert
                rawUnitPrice = Number.parseFloat(currencyPrice.currency_price);
                this._formattedUnitPrice = this._currencyFormat.format(rawUnitPrice, this.showExtendedPrecisionPrice, this.currency, this._currencyLocale);
            } else {
                // Default to USD price if selected currency can not be found - this is always the case when converting with exchange rates
                currencyPrice = tieredPrice.price_list.find(currencyPrice => currencyPrice.currency === 'USD');
                rawUnitPrice = Number.parseFloat(currencyPrice.currency_price);
                if (this._currencyExchangeRates[this.currency]) {
                    this._formattedUnitPrice = this._currencyFormat.convertAndFormat(rawUnitPrice, this._currencyExchangeRates[this.currency], this.showExtendedPrecisionPrice, this.currency, this._currencyLocale);
                    // for metrics, AFTER FORMATTING, convert unit price to currency price via exchange rate
                    // by using the utility to format with no symbol using en-US number formatting, then strip
                    // out thousands separator commas and parse to a float to leave the raw converted price
                    rawUnitPrice = Number.parseFloat(
                        this._currencyFormat.convertAndFormatNoSymbol(
                            rawUnitPrice, this._currencyExchangeRates[this.currency], this.showExtendedPrecisionPrice, this.currency, 'en-US'
                        ).replace(',', '')
                    );
                } else {
                    // exchange rate is not defined for USD
                    this._formattedUnitPrice = this._currencyFormat.format(rawUnitPrice, this.showExtendedPrecisionPrice, 'USD', this._currencyLocale);
                    metricsCurrency = 'USD';
                }
            }
        }
        this.quantityDisplay = this.quantity.toLocaleString('en-US');

        // set status and then do metrics stuff to that we're not blocked if
        // metrics doesn't work
        this._setStatus(TiMetricsDataStatus.READY);

        // capture metrics data
        let metricsData: Record<string, unknown> | string = this.hostElement.getAttribute('metrics-data');
        metricsData = (metricsData ? JSON.parse(metricsData) : {}) as Record<string, unknown>;

        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'add to cart',
            status: this.dataStatus,
            data: {
                distributor_name: 'ti store',
                ti_opn: this.opn,
                ti_gpn: this.gpn,
                part_type: this.partType,
                product_status: this.productStatus,
                element_type: this.tiMetricsLocation,
                part_carrier: this._reelPkgOption,
                part_description: this._gpnDescription,
                part_image_url: this._gpnImageUrl,
                part_quantity: this.quantity,
                part_unit_price: `${rawUnitPrice}`,
                part_url: this._opnUrl,
                currency_code: metricsCurrency,
                dien_code: this.dateCode === 'NONE' ? '' : this.dateCode,
                year: this.dateCodeYear,
                week: this.dateCodeWeek,
                pcr_code: this.pcrCode,
                ...metricsData
            }
        });
    }

    private async _checkCustomReel() {
        // if button is disabled, don't do anything
        this._checkQuantity;

        // Do a double check on the currency selection in the header
        const currencySelectionEl = document.querySelector('ti-header-currency-selection');
        if (currencySelectionEl !== null && currencySelectionEl.getAttribute('value') !== null) {
            this.currency = currencySelectionEl.getAttribute('value');
        }

        if (isNaN(this.quantity)) {
            this.quantityErrorText = this.quantityErrorText = this._localeStrings['quantityError2'];
            this.quantityError = true;
            if (this.relevantForLotDateCode && this._isDialogOpen) {
                this._setHiRelCompletion();
            }
            return;
        }

        if (this.relevantForLotDateCode) {
            if (!this._isDialogOpen) {
                this._setStatus(TiMetricsDataStatus.LOADING);
            }

            let inventoryResponse;
            try {
                const fetchInventory = await fetch(`${this.prostInventoryUrl}`, {
                    headers: {
                        'Cache-Control': 'no-store, must-revalidate',
                        'Expires': '0',
                        'Content-Type': 'application/json'
                    },
                    method: 'POST',
                    body: JSON.stringify({
                        'code': this.opn,
                        'qty': this.quantity,
                        'currency': this.currency
                    })
                });
                if (!fetchInventory.ok) {
                    throw new Error(`Received ${fetchInventory.status}`);
                }
                inventoryResponse = await fetchInventory.json();
            } catch (err) {
                this._addToCart();
                this.tiMetricsAction.emit({
                    elementName: this.hostElement.tagName,
                    eventName: 'hiRel',
                    status: TiMetricsDataStatus.ERROR,
                    error: err.message
                });
            }

            if (!inventoryResponse || inventoryResponse.length === 0) {  // json is an array of fulfillment objects
                this._addToCart();
            }

            if (inventoryResponse && inventoryResponse.dienOptions !== null) {
                // Add the default no date code selected value
                this.dienLabels = this._localeStrings['noDateCode'] + ',';
                this._dienOptions = 'NONE,';

                this.dateCodeYearOptions = this._localeStrings['year'] + ',';

                this._dateCodeLabel = this._localeStrings['noDateCode'];
                this._dateCodeFee = this._currencyFormat.format(0, false, this.currency, this._currencyLocale);

                inventoryResponse.dienOptions.forEach(dienOption => {
                    this._dienOptions += dienOption.dienCode + ',';

                    //8402 dropdown less than options set here!

                    if (dienOption.dienCode.indexOf('SLDC-') !== -1) {
                        this.dienLabels += this._localeStrings['singleLotYear'].replace('#', dienOption.dienCode.replace(/[^0-9]/g, ''))
                            .replace('{}', this._currencyFormat.format(dienOption.dienPrice, false, this.currency, this._currencyLocale).replace(/,/g, '\\,')) + ',';
                    } else if (dienOption.dienCode.indexOf('DC-') !== -1) {
                        this.dienLabels += this._localeStrings['lessThanYear'].replace('#', dienOption.dienCode.replace(/[^0-9]/g, ''))
                            .replace('{}', this._currencyFormat.format(dienOption.dienPrice, false, this.currency, this._currencyLocale).replace(/,/g, '\\,')) + ',';
                    } else {
                        this.dienLabels += this._localeStrings['specificDateCode']
                            .replace('{}', this._currencyFormat.format(dienOption.dienPrice, false, this.currency, this._currencyLocale).replace(/,/g, '\\,')) + ',';

                        dienOption.dateCodes.forEach(dateCode => {
                            // Need to force an array here in case of only 1 week entry in a year
                            this._dateCodes[dateCode.year] = [];
                            dateCode.weeks.forEach(week => {
                                this._dateCodes[dateCode.year].push(week);
                            });

                            this.dateCodeYearOptions += dateCode.year + ',';
                        });

                        this._pcrCodes = dienOption.pcrCodes;
                    }
                });

                this.dienLabels = this.dienLabels.slice(0, -1);
                this._dienOptions = this._dienOptions.slice(0, -1);
                this.dateCodeYearOptions = this.dateCodeYearOptions.slice(0, -1);
            } else {
                // Add the default no date code selected value
                this.dienLabels = this._localeStrings['noDateCode'];
                this._dienOptions = 'NONE';
            }
            // default to no date code selected value
            this.dateCode = 'NONE';

            this._setHiRelCompletion();
            return;
        }

        if (!this.useCustomReelApi) {
            this._addToCart();
            return;
        }

        this._setStatus(TiMetricsDataStatus.LOADING);
        let customReelsResponse;
        try {
            const fetchCustomReels = await fetch(this.customReelsApiUrl, {
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                },
                method: 'POST',
                body: JSON.stringify([{
                    'orderable_material': this.opn,
                    'quantity_requested': this.quantity,
                    'spq': this.packageQuantity,
                    'custom_reel_enabled': this.relevantForCustomReel
                }])
            });

            if (fetchCustomReels.status === 401) {
                this._atcApiRespErrorType = AddToCartResponseErrorType.UNAUTHORIZED;
                this._setStatus(TiMetricsDataStatus.ERROR);
                throw new Error(`Received 401 un-authorized for fetch api. User session invalid.`);
            } else if (fetchCustomReels.status === 400) {
                this._atcApiRespErrorType = AddToCartResponseErrorType.GENERIC;
                this._setStatus(TiMetricsDataStatus.ERROR);
                throw new Error(`Received 400 for bad request.`);
            } else if (!fetchCustomReels.ok) {
                this._addToCart();
                throw new Error(`Received ${fetchCustomReels.status}`);
            }

            customReelsResponse = await fetchCustomReels.json();
            // !!! Check for a proper response including fulfillment data !!!
            if (customReelsResponse && customReelsResponse.length) {  // json is an array of properties and a 'fulfilment' object
                this._customReel = customReelsResponse[0]['custom_reel']; // boolean
                this._fullReel = customReelsResponse[0]['full_reel']; // number

                if (customReelsResponse[0]['fulfilment'] && customReelsResponse[0]['fulfilment']['cut_tape'] != undefined) {
                    this._reelPkgOption = customReelsResponse[0]['fulfilment']['cut_tape'] === 0 ? 'FRP' : 'CTX';
                }
            }

            if (!this._customReel) {
                this._addToCart();
            } else {
                const remainder_reel = (this.quantity % this.packageQuantity);
                this._reelCustom = remainder_reel;
                this._reelCutTape = remainder_reel;

                this._setStatus(TiMetricsDataStatus.READY);
            }

        } catch (err) {
            this._reelPkgOption = 'CTX';
            this._setStatus(TiMetricsDataStatus.ERROR);
            this.tiMetricsAction.emit({
                elementName: this.hostElement.tagName,
                eventName: 'custom reels',
                status: TiMetricsDataStatus.ERROR,
                error: err.message
            });
        }
    }

    private _checkPCRCode(event) {
        this.pcrCodeError = true;

        this._pcrCodes.forEach(pcrCode => {
            if (pcrCode.substring(0, 7) === event.detail.value) {
                this.pcrCodeError = false;
                this.pcrCode = event.detail.value;
            }
        });

        this._setHiRelCompletion();
    }

    private _checkQuantity(value): boolean {
        if (value) {
            return this._verifyQuantity(Number.parseInt(value.replace(/,/g, '')));
        } else {
            return this._verifyQuantity(0);
        }
    }

    /**
     * Close dialog method.
     */
    private _closeDialog() {
        // close dialog
        this._dialogService.closeDialog();
    }

    private _codeChoiceUpdate() {
        this.dateCodeYear = '';
        this.dateCodeWeek = '';
        this.pcrCode = '';
        this.isDateCode = !this.isDateCode;
        this._setHiRelCompletion();
    }

    private _customReelSelectionUpdate(event: CustomEvent) {
        if (event.detail.value === this._localeStrings['customReel']) {
            this._reelPkgOption = 'CRP';
        } else {
            this._reelPkgOption = 'CTX';
        }

        if (event.detail.value === this._localeStrings['customReel']) {
            //TODO: DC_FOLDER_ARCH-8989 Change this hardcoding to something better
            // hardcoded to $7.00 USD flat fee if no customReelFee is provided
            if (this.customReelFee === undefined) {
                this.customReelFeeMessage = this._localeStrings['customReelFeeMessage'].replace('{}', '$7.00');
            } else {
                const customReelFeesByCurrency = JSON.parse(this.customReelFee) as CurrencyPricing[];
                let currencyPrice = customReelFeesByCurrency.find(currencyPrice => currencyPrice.currency === this.currency);
                let currency = this.currency;
                // Default to USD if selected currency can not be found in API return
                if (currencyPrice === undefined) {
                    currencyPrice = customReelFeesByCurrency.find(currencyPrice => currencyPrice.currency === 'USD');
                    currency = 'USD';
                }

                const customReelFeePrice = this._currencyFormat.format(Number.parseFloat(currencyPrice.currency_price), false, currency, this._currencyLocale);
                this.customReelFeeMessage = this._localeStrings['customReelFeeMessage'].replace('{}', customReelFeePrice);
            }
        } else {
            this.customReelFeeMessage = '';
        }

        // update the custom reel fee message using text content
        // on toggling the dropdown selection
        this._customReelFeeElement.textContent = this.customReelFeeMessage;

        this.tiMetricsAction.emit({
            context: `${this.opn}`,
            elementName: this.hostElement.tagName,
            eventName: 'custom reel selection',
            data: {
                value: event.detail.value
            }
        });
    }

    /**
     * Update date code selection event
     *
     * @private
     * @param {CustomEvent} event
     * @memberof AddToCart
     */
    private _dateCodeUpdate(event: CustomEvent) {
        this.dateCode = event.detail.value;
        if (event.detail.value !== '') {
            const dateCodeText = event.detail.label.split('(');
            this._dateCodeLabel = dateCodeText[0].trim();
            this._dateCodeFee = dateCodeText[1].replace(/[+)]/g, '').trim();
        } else {
            this._dateCodeLabel = event.detail.label;
            this._dateCodeFee = this._currencyFormat.format(0, false, this.currency, this._currencyLocale);
        }
        this._setHiRelCompletion();

        this.tiMetricsAction.emit({
            context: `${this.opn}`,
            elementName: this.hostElement.tagName,
            eventName: 'date code selection',
            data: {
                value: event.detail.value
            }
        });
    }

    private _dateCodeYearUpdate(event: CustomEvent) {
        if (this.dateCodeYear !== event.detail.value) {
            this.dateCodeWeek = '';
            this.dateCodeWeekOptions = this._localeStrings['week'] + ',';
            this.dateCodeYear = event.detail.value;

            this._dateCodes[this.dateCodeYear].forEach(dateCode => {
                this.dateCodeWeekOptions += dateCode.week + ',';
            });

            this.dateCodeWeekOptions = this.dateCodeWeekOptions.slice(0, -1);

            this._setHiRelCompletion();

            if (this._selectElement) {
                this._selectElement.value = this._localeStrings['week'];
            }
        }

        this.tiMetricsAction.emit({
            context: `${this.opn}`,
            elementName: this.hostElement.tagName,
            eventName: 'date code year selection',
            data: {
                value: event.detail.value
            }
        });
    }

    private _dateCodeWeekUpdate(event: CustomEvent) {
        this.dateCodeWeek = event.detail.value;
        this._setHiRelCompletion();

        this.tiMetricsAction.emit({
            context: `${this.opn}`,
            elementName: this.hostElement.tagName,
            eventName: 'date code week selection',
            data: {
                value: event.detail.value
            }
        });
    }

    /**
     * return add-to-cart dialog content
     */
    private _getAddToCartDialogContent() {
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">
                {this.successHeader}
            </h2>,
            <div class="ti-add-to-cart-dialog-body">
                <div class="ti-add-to-cart-dialog-product-synopsis">
                    <div class={`ti-add-to-cart-dialog-image ${!this._gpnImageUrl ? 'ti-add-to-cart-dialog-no-image-icon' : ''}`}>
                        <ti-image
                            src={this._gpnImageUrl || this._noImagePlaceholder}
                            src-default={this._noImagePlaceholder}
                            ratio="square"
                        ></ti-image>
                    </div>
                    <div class="ti-add-to-cart-dialog-info">
                        <h5>
                            <a id="add_to_cart_modal_partNumber" href={this._opnUrl}>{this.opn}</a>
                        </h5>
                        <div>
                            <p innerHTML={this._gpnDescription}></p>
                            <p class="ti-add-to-cart-dialog-dialog-info-item">{this.quantityLabel}: {this.quantityDisplay}</p>
                            <p class="ti-add-to-cart-dialog-dialog-info-item" id="e2e-unit-price">
                                {
                                    this.showCurrencyCode ?
                                        (`${this.unitPriceLabel}: ${this.priceOverrideText ? this.priceOverrideText : this._formattedUnitPrice} (${this.currency})`)
                                        : (`${this.unitPriceLabel}: ${this.priceOverrideText ? this.priceOverrideText : this._formattedUnitPrice}`)
                                }
                            </p>
                        </div>
                        {!this.hiRelIncomplete &&
                            <div>
                                {
                                    this.dateCode !== 'SPECIFIC-DC' ?
                                        <p id="date-code-selection">{this._localeStrings['dateCode'] + ': ' + this._dateCodeLabel}</p>
                                        : [
                                            <p id="date-code-selection">{this._dateCodeLabel}</p>,
                                            this.isDateCode ?
                                                <p id="date-code">{this._localeStrings['dateCode'] + ': ' + this.dateCodeYear.slice(-2) + this.dateCodeWeek}</p>
                                                :
                                                <p id="date-code">{this._localeStrings['pcrCode'] + ': ' + this.pcrCode}</p>
                                        ]
                                }
                                <p id="service-fee">{this._localeStrings['serviceFee'] + ': ' + this._dateCodeFee}</p>
                            </div>
                        }
                    </div>
                </div>
            </div>,
            <div slot="action" class="ti-add-to-cart-dialog-action">
                <ti-button id="add_to_cart_modal_checkout" appearance="primary">
                    <a href={this.completeCheckoutUrl}>{this.checkoutLabel}</a>
                </ti-button>
                <ti-button id="add_to_cart_modal_continue" appearance="secondary" onClick={() => this._closeDialog()}>
                    {this.continueShoppingLabel}
                </ti-button>
            </div>
        ]);
    }

    /**
     * return custom reel dialog content
     */
    private _getCustomReelDialogContent() {
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this._localeStrings['selectCarrier']}</h2>,
            <div class="ti-add-to-cart-dialog-content">
                <div class="ti-add-to-cart-dialog-left" data-lid="carrier_option_modal">
                    <div class="ti-add-to-cart-dialog-body">
                        <ti-multicolumn-list compact>
                            <ti-multicolumn-list-row class="ti-add-to-cart-dialog-header">
                                <ti-multicolumn-list-cell>
                                    <span class="ti-add-to-cart-dialog-carrier-heading">{this._localeStrings['carrier']}</span>
                                </ti-multicolumn-list-cell>
                                <ti-multicolumn-list-cell>
                                    <span class="ti-add-to-cart-dialog-carrier-heading">{this._localeStrings['quantity']}</span>
                                </ti-multicolumn-list-cell>
                            </ti-multicolumn-list-row>
                            {this._fullReel > 0 ? ([
                                <ti-multicolumn-list-row>
                                    <ti-multicolumn-list-cell>
                                        {this._localeStrings['fullReel']}
                                    </ti-multicolumn-list-cell>
                                    <ti-multicolumn-list-cell>
                                        {this._fullReel} x {this._intlNumberFormat(this.packageQuantity)}
                                    </ti-multicolumn-list-cell>
                                </ti-multicolumn-list-row>
                            ]) : ''}
                            {(this._reelCutTape > 0 && this._reelCustom > 0) ? ([
                                <ti-multicolumn-list-row>
                                    <ti-multicolumn-list-cell>
                                        <ti-select
                                            id="e2e-custom-reel"
                                            value={this._localeStrings['cutTape']}
                                            options={this._localeStrings['cutTape'] + ',' + this._localeStrings['customReel']}
                                            onTiChange={ev => this._customReelSelectionUpdate(ev)}>
                                        </ti-select>
                                        <div class="ti-add-to-cart-dialog-custom-reel-fee" ref={el => this._customReelFeeElement = el}></div>
                                    </ti-multicolumn-list-cell>
                                    <ti-multicolumn-list-cell id="custom-reel-choice-amount">
                                        {this._intlNumberFormat(this._reelCustom)}
                                    </ti-multicolumn-list-cell>
                                </ti-multicolumn-list-row>
                            ]) : ([
                                <ti-multicolumn-list-row>
                                    {this._reelCutTape > 0 ? ([
                                        <ti-multicolumn-list-cell>{this._localeStrings['cutTape']}</ti-multicolumn-list-cell>,
                                        <ti-multicolumn-list-cell>{this._reelCutTape}</ti-multicolumn-list-cell>
                                    ]) : ''}
                                    {this._reelCustom > 0 ? ([
                                        <ti-multicolumn-list-cell>{this._localeStrings['customReel']}</ti-multicolumn-list-cell>,
                                        <ti-multicolumn-list-cell>{this._reelCustom}</ti-multicolumn-list-cell>
                                    ]) : ''}
                                </ti-multicolumn-list-row>
                            ])}

                            <ti-multicolumn-list-row>
                                <ti-multicolumn-list-cell>
                                    {this._localeStrings['totalQuantity']}
                                </ti-multicolumn-list-cell>
                                <ti-multicolumn-list-cell>
                                    {this._intlNumberFormat(this.quantity)}
                                </ti-multicolumn-list-cell>
                            </ti-multicolumn-list-row>
                        </ti-multicolumn-list>
                    </div>
                </div>
            </div>,
            <div slot="action" class="ti-add-to-cart-dialog-action">
                <ti-button appearance="primary" onClick={(ev) => { this._addToCart(); (ev.currentTarget as HTMLElement).blur(); }}>
                    <a>{this.continueLabel}</a>
                </ti-button>
                <ti-button appearance="secondary" onClick={() => this._closeDialog()}>
                    {this._localeStrings['cancel']}
                </ti-button>
            </div>
        ]);
    }

    private _getHirelDialogContent() {
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this._localeStrings['hiRelTitle']}</h2>,
            <div class="ti-add-to-cart-dialog">
                <div class="ti-add-to-cart-dialog-left" data-lid="hirel_option_modal">
                    <div class="ti-add-to-cart-dialog-body">
                        <p innerHTML={this._localeStrings['hiRelDescription']}></p>

                        <ti-multicolumn-list compact>
                            <ti-multicolumn-list-row>
                                <ti-multicolumn-list-cell>
                                    {this._localeStrings['partNumber']}
                                </ti-multicolumn-list-cell>
                                <ti-multicolumn-list-cell>
                                    {this.opn}
                                </ti-multicolumn-list-cell>
                            </ti-multicolumn-list-row>

                            <ti-multicolumn-list-row>
                                <ti-multicolumn-list-cell>
                                    {this._localeStrings['quantity']}
                                </ti-multicolumn-list-cell>
                                <ti-multicolumn-list-cell>
                                    <ti-form-element error-text={this.quantityErrorText} hint-text={this.hideQuantityHintText ? undefined : this.quantityHintText} error={this.quantityError}>
                                        <ti-input
                                            pattern="[0-9]*"
                                            value={isNaN(this.quantity) ? '' : this.quantity.toString()}
                                            placeholder={this.quantityPlaceholderText}
                                            onTiChange={ev => this._checkQuantity(ev.detail?.value)}
                                            onTiFocus={() => this._onQuantityInputFocus()}
                                            onKeyUp={ev => this._handleKeyup(ev)}>
                                        </ti-input>
                                    </ti-form-element>
                                </ti-multicolumn-list-cell>
                            </ti-multicolumn-list-row>

                            <ti-multicolumn-list-row>
                                <ti-multicolumn-list-cell>
                                    {this._localeStrings['dateCode']}
                                </ti-multicolumn-list-cell>
                                <ti-multicolumn-list-cell>
                                    <ti-select
                                        id="date-code"
                                        value={this.dateCode}
                                        labels={this.dienLabels}
                                        options={this._dienOptions}
                                        onTiSelectChange={ev => this._dateCodeUpdate(ev)}>
                                    </ti-select>
                                    <div style={{ padding: '1rem 0 32px 0' }}>
                                        <ti-tooltip-trigger>
                                            <span>{this._localeStrings['dateCodeDescription']}<ti-svg-icon size="xs" style={{ marginLeft: '.25rem' }}></ti-svg-icon></span>
                                            <ti-tooltip position="bottom">
                                                {this._localeStrings['dateCodeTooltip']}
                                            </ti-tooltip>
                                        </ti-tooltip-trigger>
                                    </div>
                                </ti-multicolumn-list-cell>
                            </ti-multicolumn-list-row>

                            {this.dateCode === 'SPECIFIC-DC' && [
                                this.isDateCode ? (
                                    <ti-multicolumn-list-row class="ti-multicolumn-list-row-compact">
                                        <ti-multicolumn-list-cell>
                                            {this._localeStrings['lotCode']}
                                        </ti-multicolumn-list-cell>
                                        <ti-multicolumn-list-cell>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <ti-select
                                                    style={{ marginRight: '1rem' }}
                                                    id="date-code-year"
                                                    value={this._localeStrings['year']}
                                                    options={this.dateCodeYearOptions}
                                                    disabledOptions={this._localeStrings['year']}
                                                    hiddenOptions={this._localeStrings['year']}
                                                    onTiChange={ev => this._dateCodeYearUpdate(ev)}>
                                                </ti-select>
                                                {this.dateCodeYear && (
                                                    <ti-select
                                                        id="date-code-week"
                                                        value={this._localeStrings['week']}
                                                        options={this.dateCodeWeekOptions}
                                                        disabledOptions={this._localeStrings['week']}
                                                        hiddenOptions={this._localeStrings['week']}
                                                        onTiChange={ev => this._dateCodeWeekUpdate(ev)}
                                                        ref={(el) => this._selectElement =el}>
                                                    </ti-select>)}
                                            </div>
                                        </ti-multicolumn-list-cell>
                                    </ti-multicolumn-list-row>
                                ) : (
                                    <ti-multicolumn-list-row class="ti-multicolumn-list-row-compact">
                                        <ti-multicolumn-list-cell>
                                            {this._localeStrings['pcrCode']}
                                        </ti-multicolumn-list-cell>
                                        <ti-multicolumn-list-cell>
                                            <div>
                                                <ti-form-element error-text={this._localeStrings['pcrCodeError']} error={this.pcrCodeError}>
                                                    <ti-input
                                                        pattern="^\w{0,7}$"
                                                        onTiChange={ev => this._checkPCRCode(ev)}>
                                                    </ti-input>
                                                </ti-form-element>
                                                <div style={{ padding: '0.5rem 0 0 0' }}>
                                                    <ti-tooltip-trigger>
                                                        <span>{this._localeStrings['pcrCodeDescription']}<ti-svg-icon size="xs" style={{ marginLeft: '.25rem' }}></ti-svg-icon></span>
                                                        <ti-tooltip position="auto">
                                                            {this._localeStrings['pcrCodeTooltip']}
                                                        </ti-tooltip>
                                                    </ti-tooltip-trigger>
                                                </div>
                                            </div>
                                        </ti-multicolumn-list-cell>
                                    </ti-multicolumn-list-row>
                                ),
                                <ti-multicolumn-list-row class="ti-multicolumn-list-row-compact">
                                    <ti-multicolumn-list-cell></ti-multicolumn-list-cell>
                                    <ti-multicolumn-list-cell style={{ padding: '0 0 0 1rem' }}>
                                        <ti-button appearance="text" onClick={() => this._codeChoiceUpdate()}>
                                            {this.isDateCode ? this._localeStrings['usePCR'] : this._localeStrings['useLotCode']}
                                        </ti-button>
                                    </ti-multicolumn-list-cell>
                                </ti-multicolumn-list-row>]
                            }
                        </ti-multicolumn-list>
                    </div>
                </div>
            </div>,
            <div slot="action" class="ti-add-to-cart-dialog-action">
                <ti-button slot="action" appearance="primary" disabled={this.hiRelIncomplete} onClick={(ev) => { this._addToCart(); (ev.currentTarget as HTMLElement).blur(); }}>
                    <a>{this.continueLabel}</a>
                </ti-button>
                <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()}>
                    {this._localeStrings['cancel']}
                </ti-button>
            </div>
        ]);
    }

    /**
     * return error dialog content
     */
    private _getErrorDialogContent() {

        if (this._atcApiRespErrorType === AddToCartResponseErrorType.CART_FULL) {
            return this._getErrorDialogContentOnCartFull();
        }
        else if (this._atcApiRespErrorType === AddToCartResponseErrorType.UNAUTHORIZED) {
            return this._getErrorDialogContentOnUnauthorized();
        }
        else {
            return this._getErrorDialogContentGeneric();
        }
    }

    private _getErrorDialogContentGeneric() {
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this.failHeader}</h2>,
            <p innerHTML={this._failMessage}></p>,
            <div slot="action"></div>
        ]);
    }

    private _getErrorDialogContentOnCartFull() {
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this._fullCartErrorDialogTitle}</h2>,
            <p innerHTML={this._failMessageOnCartFull}></p>,
            <div slot="action" class="ti-add-to-cart-dialog-action">
                <ti-button id="add_to_cart_view_cart" appearance="primary">
                    <a href={this.completeCheckoutUrl}>{this._viewCartButtonLabel}</a>
                </ti-button>
                <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()}>
                    {this._localeStrings['cancel']}
                </ti-button>
            </div>
        ]);
    }

    private _getErrorDialogContentOnUnauthorized() {
        const sanitizedCurrentLocation = encodeURIComponent(window.location.href.replace('&login-check=true',''));
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this.failHeader}</h2>,
            <p innerHTML={this._failMessageOnUnauthorized}></p>,
            <div slot="action" class="ti-add-to-cart-dialog-action">
                <ti-button id="add_to_cart_view_cart" appearance="primary">
                    <a href={'/secure-link-forward/?gotoUrl=' + sanitizedCurrentLocation}> {this._loginLabel}</a>
                </ti-button>
                <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()}>
                    {this._localeStrings['cancel']}
                </ti-button>
            </div>
        ]);
    }

    /**
     * return loading dialog content
     */
    private _getLoadingDialogContent() {
        return ([
            <div slot="title"></div>,
            <div class="ti-add-to-cart-dialog-default-size">
                <div class="ti-add-to-cart-dialog-loader-container">
                    <ti-progress class="ti-add-to-cart-dialog-center"></ti-progress>
                </div>
            </div>,
            <div slot="action"></div>
        ]);
    }

    /**
     * handle keyup event on quantity field input element
     */
    private _handleKeyup(event: KeyboardEvent) {
        if (event.key === 'Enter' && !this.quantityError) {
            this._checkCustomReel();
        }
    }

    /**
     * formats simple numbers by locale (inserts commas as thousands separator)
     */
    private _intlNumberFormat(num: number) {
        if (!Intl || !Intl.NumberFormat) {
            if (num != null) {
                return num.toLocaleString(this.locale);
            }
        } else {
            return new Intl.NumberFormat(this.locale).format(num);
        }
    }

    /**
     * Emit metrics action event for quantity onFocus event
     */
    private _onQuantityInputFocus() {
        this.tiMetricsAction.emit({
            context: this.opn,
            elementName: this.hostElement.tagName,
            eventName: 'focus quantity input'
        });
    }

    /**
     * Opens content dialog related to carrier options
     *
     */
    private _openCarrierDialog() {
        if (!this._dialogService) {
            const win = (typeof (window as any) !== 'undefined' ? window : {}) as any;
            if (win.com && win.com.TI && win.com.TI.FeatureDialogService) {
                this._dialogService = win.com.TI.FeatureDialogService;
            }
        }

        // return if dialog service is not accessible
        if (!this._dialogService) {
            return;
        }

        const carrierDialogContent = ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this._localeStrings['carrierDialogTitle']}</h2>,
            <div innerHTML={this._localeStrings['carrierDialogMessage']}></div>,
            <a href={this._localeStrings['customReelLearnMoreUrl']} target="_blank">{this._localeStrings['customReelLearnMore']}</a>
        ]);

        this._openDialog(carrierDialogContent);
    }

    /**
     * Opens content dialog related to sku
     *
     */
    private _openSkuDialog() {
        if (!this._dialogService) {
            const win = (typeof (window as any) !== 'undefined' ? window : {}) as any;
            if (win.com && win.com.TI && win.com.TI.FeatureDialogService) {
                this._dialogService = win.com.TI.FeatureDialogService;
            }
        }

        // return if dialog service is not accessible
        if (!this._dialogService) {
            return;
        }

        const skuDialogContent = ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this._localeStrings['skuDialogTitle']}</h2>,
            <p>{this._localeStrings['skuDialogMessage']}</p>,
            <a href={this._localeStrings['skuDialogLearnMore']} target="_blank">{this._localeStrings['customReelLearnMore'] }</a>
        ]);

        this._openDialog(skuDialogContent);
    }

    /**
     * open dialog using new launcher service
     * @param dialogContent
     */
    private _openDialog(dialogContent) {
        const dialogConfig = {
            class: 'ti-add-to-cart-dialog',
            content: dialogContent,
            modal: true
        };

        this._dialogService.openDialog(dialogConfig);
    }

    /**
     * Calculate & populate the OPN PDP page link and the store cart page link
     */
    private async _populateCTALinks() {
        this.completeCheckoutUrl = `/store/${this.locale.toLowerCase()}/cart`;
        this._opnUrl = `/product/${this._languageCode}${this.gpn}/part-details/${this.opn}`;
    }

    /**
     * Set a local reference to com.TI.CurrencyFormat.
     */
    private _setCurrencyFormat() {
        // accessing the global utility function for formatting from window variable
        const com = (window as any).com;
        if (com && com.TI && com.TI.CurrencyFormat) {
            this._currencyFormat = com.TI.CurrencyFormat;
        }
    }

    /**
     * Sets the error attribute and
     * if inStickyColumn
     * toggles the parent TR height according to the height change
     */
    private _setError(bool: boolean) {
        //Check if we're setting a new boolean value
        if (this.quantityError != bool) {
            this.quantityError = bool;
            if (this.inStickycolumn) {
                const tr = this.hostElement.closest('tr');
                //Check to see if a TR parent exists
                if (!tr)
                    return;

                //Set initial height for height to be restored when message disappears
                if (this._initialHeight === undefined)
                    this._initialHeight = this.hostElement.offsetHeight;

                const currentHeight = this.hostElement.offsetHeight;
                if (bool) {
                    // Need timeout to wait till animation finishes before calculating height
                    setTimeout(() => {
                        tr.style.height = tr.offsetHeight + (this.hostElement.offsetHeight - currentHeight) + 'px';
                    }, 20);
                } else {
                    tr.style.height = this._initialHeight + 'px';
                }
            }
        }
    }

    /**
     * Sets the hi-rel completion status based on various hi-rel selections
     *
     * @private
     * @memberof AddToCart
     */
    private _setHiRelCompletion() {
        this.hiRelIncomplete = true;

        if (!this.quantityError) {
            if (this.dateCode !== 'SPECIFIC-DC') {
                this.hiRelIncomplete = false;
            } else if (this.isDateCode) {
                if (this.dateCodeYear && this.dateCodeWeek) {
                    this.hiRelIncomplete = false;
                } else {
                    this.hiRelIncomplete = true;
                }
            } else {
                if (!this.pcrCodeError && this.pcrCode) {
                    this.hiRelIncomplete = false;
                } else {
                    this.hiRelIncomplete = true;
                }
            }
        }

        // Need to force a refresh on the dialog content to display new fields
        this._updateDialogContent(TiMetricsDataStatus.READY);
    }

    /**
     * Sets the MetricDataStatus and applies visual transitions between states in the modal
     */
    private _setStatus(status: TiMetricsDataStatus) {
        this.dataStatus = status;
        this._updateDialogContent(status);
    }

    /**
     * Render add-to-list component
     */
    private _showAddToListComponent() {
        if (!this.storeKey && this.showAddToList) {
            const atl = (
                <ti-add-to-myti-list
                    appearance={`secondary ${this.viewportSize !== TiResponsiveViewport.phone ? 'icon-only' : ''} ${this.compactButton ? 'compact' : ''}`}
                    domain={window.location.origin}
                    initialMode='list-search'
                    locale={this.locale}
                    opn={this.opn}
                    qtyLimit={this.quantityLimit}
                    quantity={this.quantity}
                    disabled={this.quantityError}>
                </ti-add-to-myti-list>
            );
            return this.viewportSize === TiResponsiveViewport.phone
                ? <div class="ti-add-to-cart-list-btn-container-phone">{atl}</div>
                : atl;
        }
        return null;
    }

    /**
     * update dialog content based on status flag
     * @param {TiMetricsDataStatus} status
     */
    private _updateDialogContent(status: TiMetricsDataStatus) {
        if (!this._dialogService) {
            const win = (typeof (window as any) !== 'undefined' ? window : {}) as any;
            if (win.com && win.com.TI && win.com.TI.FeatureDialogService) {
                this._dialogService = win.com.TI.FeatureDialogService;
            }
        }

        // return if dialog service is not accessible
        if (!this._dialogService) {
            return;
        }

        let dialogContent;

        switch (status) {
            case TiMetricsDataStatus.ERROR:
                dialogContent = this._getErrorDialogContent();
                break;
            case TiMetricsDataStatus.LOADING:
                dialogContent = this._getLoadingDialogContent();
                break;
            case TiMetricsDataStatus.READY:
                // show custom reel dialog content if custom reel is is avaiable and is not complete
                // show add to cart dialog content if no custom reel/custom reel completed and data ready
                if (this.relevantForLotDateCode) {
                    dialogContent = this.completedHiRel ? this._getAddToCartDialogContent() : this._getHirelDialogContent();
                } else {
                    dialogContent = this.completedCustomReel ? this._getAddToCartDialogContent() : this._getCustomReelDialogContent();
                }
                break;
        }

        // check if dialog content is valid
        if (dialogContent) {
            // check if dialog is already open
            if (this._isDialogOpen) {
                // if dialog is open, then update the dialog content
                this._dialogService.updateDialogContent(dialogContent);
            } else {
                // if dialog is not open, then open a fresh dialog
                this._openDialog(dialogContent);
                this._isDialogOpen = true;
            }
        }
    }

    private _verifyQuantity(newQty?: number) {
        let hasError = false;
        //Only validate newQty if it's defined
        if (newQty !== undefined) {
            if (newQty <= 0) {
                this.quantityErrorText = this._localeStrings['quantityError2'];
                hasError = true;
            } else if (this.quantityLimit && newQty > this.quantityLimit) {
                this.quantityErrorText = this._localeStrings['quantityError'];
                hasError = true;
            } else if (this.orderLimit && newQty > this.orderLimit) {
                this.quantityErrorText = this._localeStrings['quantityError3'];
                this.quantityErrorText = this.quantityErrorText.replace('{}', this.orderLimit.toLocaleString());
                hasError = true;
            } else {
                this.quantity = newQty;

                // keep input element in sync across external qty updates due to sorting etc
                if (this._inputElement && this.quantity) {
                    this._inputElement.value = this.quantity;
                }

                hasError = false;
            }
        }

        this._setError(hasError);

        if (this.relevantForLotDateCode && this._isDialogOpen) {
            // Need to keep add-to-cart input element in sync with the quantity inside the modal
            this._inputElement.value = isNaN(this.quantity) ? '' : this.quantity;

            // Need to force a refresh on the dialog content to display new fields
            this._checkCustomReel();
        }

        return hasError;
    }

    render() {
        if (this.buttonStatus === AddToCart._BUTTON_STATUS_TYPE.ERROR) {
            this.hostElement.innerHTML = '<!-- ti-add-to-cart status error -->';
            return (null);
        } else {
            const elements = [
                <ti-form-element error-text={this.quantityErrorText} hint-text={this.hideQuantityHintText ? undefined : this.quantityHintText} error={this.quantityError}>
                    <ti-input ref={el => this._inputElement = el}
                        pattern="[0-9]*"
                        value={isNaN(this.quantity) ? '' : this.quantity.toString()}
                        placeholder={this.quantityPlaceholderText}
                        onTiChange={ev => this._checkQuantity(ev.detail?.value)}
                        onTiFocus={() => this._onQuantityInputFocus()}
                        onKeyUp={ev => this._handleKeyup(ev)}>
                    </ti-input>
                </ti-form-element>,
                <div class="ti-add-to-cart-btn-container">
                    <ti-button appearance={this.compactButton ? this.appearance + ' compact' : this.appearance} disabled={this.quantityError} onClick={() => this._checkCustomReel()}>
                        {this.callToActionLabel}
                    </ti-button>
                    { this.viewportSize !== TiResponsiveViewport.phone && this._showAddToListComponent()}
                </div>,
                this.viewportSize === TiResponsiveViewport.phone && this._showAddToListComponent(),
                <div class="ti-add-to-cart-message">
                    {
                        !this.hideCarrierSkuMessage && this.customReelMessage !== '' && !this.relevantForLotDateCode && (
                            <div class={'ti-add-to-cart-custom-reel ' + ((this.locale == 'zh-CN' || this.locale == 'ja-JP') ? 'ti-add-to-cart-custom-reel-cnjp' : '')}>
                                <ti-button appearance="link" onClick={() => this._openCarrierDialog()} innerHTML={this.customReelMessage}></ti-button>
                            </div>
                        )
                    }
                    {
                        !this.hideCarrierSkuMessage && this.relevantForLotDateCode && (
                            <div class={'ti-add-to-cart-sku-selection ' + ((this.locale == 'zh-CN' || this.locale == 'ja-JP') ? 'ti-add-to-cart-sku-selection-cnjp' : '')}>
                                <ti-button appearance="link" onClick={() => this._openSkuDialog()}>{this._localeStrings['skuCodeSelection']}</ti-button>
                            </div>
                        )
                    }
                </div>
            ];

            return (
                <Host class={{ 'ti-add-to-cart-inline': this.inline }}>
                    {this.showQuantityInput ? elements : elements.slice(1, elements.length)}
                </Host>
            );
        }
    }
}
